import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { PageHead, RangePicker, monthStartIso, todayIso, addDaysIso } from "@/components/erp/ErpUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { printPdfBlob, preopenForIOS } from "@/lib/print";
import { ClipboardList, Loader2, Printer, ArrowLeft } from "lucide-react";

const PRESETS = [["Hoje", () => [todayIso(), todayIso()]], ["Semana", () => [addDaysIso(-6), todayIso()]], ["Mês", () => [monthStartIso(), todayIso()]]];
const SCOPES = [["all", "OS total"], ["delivered", "OS entregues"]];

function Row({ label, value, strong, red, testid }) {
  return (
    <div data-testid={testid} className={`flex items-center justify-between gap-3 py-2 border-t border-zinc-800 ${strong ? "font-bold text-base" : ""}`}>
      <span className="text-zinc-400 text-xs uppercase font-mono-caps tracking-widest">{label}</span>
      <span className={`${red ? "text-red-300" : strong ? "text-green-neon" : "text-zinc-100"} tabular-nums`}>{value}</span>
    </div>
  );
}
function Card({ title, color, children, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border bg-zinc-950/70 flex-1 min-w-[260px] ${color}`}>
      <div className={`px-4 py-2 font-display text-sm font-bold uppercase tracking-widest text-white rounded-t-2xl ${color.replace("border-", "bg-")}`}>{title}</div>
      <div className="px-4 pb-3">{children}</div>
    </div>
  );
}

export default function OwnerReportSummary() {
  const [range, setRange] = useState(PRESETS[2][1]());
  const [scope, setScope] = useState("all");
  const [data, setData] = useState(null);
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    setData(null);
    api.get("/reports/summary", { params: { from: range[0], to: range[1], scope } }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, [range, scope]);
  const printPdf = async () => {
    const win = preopenForIOS();
    setPrinting(true);
    try {
      const resp = await api.get("/reports/summary/pdf", { params: { from: range[0], to: range[1], scope }, responseType: "blob" });
      printPdfBlob(resp.data, win);
    } catch (e) { if (win) win.close(); toast.error(formatApiError(e)); } finally { setPrinting(false); }
  };
  const vg = data?.valor_geral; const orc = data?.total_orcado; const vei = data?.total_veiculos;
  return (
    <div className="space-y-5 animate-fade-up" data-testid="report-summary-page">
      <Link to="/owner/equipe" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-green-brand"><ArrowLeft className="h-3.5 w-3.5" /> Voltar aos relatórios</Link>
      <PageHead icon={ClipboardList} kicker="Relatórios" title="Resumo Geral"><RangePicker from={range[0]} to={range[1]} onChange={setRange} testid="rs-range" /></PageHead>
      <div className="flex flex-wrap gap-2 items-center">
        {PRESETS.map(([l, fn]) => <Button key={l} size="sm" variant="outline" data-testid={`rs-preset-${l}`} onClick={() => setRange(fn())} className="bg-zinc-900 border-zinc-800">{l}</Button>)}
        <span className="mx-2" />
        <span className="self-center text-xs text-zinc-400">Base:</span>
        {SCOPES.map(([k, l]) => <Button key={k} size="sm" data-testid={`rs-scope-${k}`} onClick={() => setScope(k)} className={scope === k ? "bg-amber-brand text-zinc-950" : "bg-zinc-900 text-zinc-300"}>{l}</Button>)}
        <div className="ml-auto">
          <Button size="sm" data-testid="rs-print" disabled={!data || printing} onClick={printPdf} className="bg-green-brand text-white">{printing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />} Imprimir / PDF</Button>
        </div>
      </div>
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 text-[11px] text-zinc-400 leading-relaxed">
        <b className="text-zinc-300">Legenda:</b> Bruto = quantidade × preço unitário (antes dos descontos). Descontos = por item + desconto total do orçamento. Líquido = bruto − descontos. T.M.V. s/ Venda = Vendas ÷ qtd. OS/Veículos. T.M.V. s/ Lucro = Lucratividade ÷ qtd. OS/Veículos. OS orçada = toda OS com orçamento no período, inclusive não aprovadas. Veículos = placas distintas das OS faturadas.
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : (
        <div className="flex flex-wrap gap-3 items-start">
          <Card title="Valor Geral" color="border-emerald-700" testid="rs-valor-geral">
            <Row testid="rs-vg-prod" label="Total Produtos (bruto)" value={brl(vg.total_produtos_bruto)} />
            <Row testid="rs-vg-serv" label="Total Serviços (bruto)" value={brl(vg.total_servicos_bruto)} />
            <Row testid="rs-vg-bruto" label="Total Geral Bruto" value={brl(vg.total_geral_bruto)} strong />
            <Row testid="rs-vg-desc" label="Total Descontos" value={`− ${brl(vg.total_descontos)}`} red />
            <Row testid="rs-vg-liq" label="Total Geral Líquido" value={brl(vg.total_geral_liquido)} strong />
          </Card>
          <Card title="Total Orçado" color="border-sky-700" testid="rs-orcado">
            <Row testid="rs-orc-qtd" label="Total Vendas Orçadas" value={`${orc.qtd} OS`} />
            <Row testid="rs-orc-lucro" label="Lucratividade (R$)" value={brl(orc.lucratividade)} strong />
            <Row testid="rs-orc-tv" label="Ticket Médio s/ Venda" value={brl(orc.ticket_venda)} />
            <Row testid="rs-orc-tl" label="Ticket Médio s/ Lucro" value={brl(orc.ticket_lucro)} />
          </Card>
          <Card title="Total de Veículos" color="border-amber-700" testid="rs-veiculos">
            <Row testid="rs-vei-qtd" label="Total Veículos" value={`${vei.veiculos} placas`} />
            <Row testid="rs-vei-vendas" label="Total Vendas (líquido)" value={brl(vei.vendas)} />
            <Row testid="rs-vei-lucro" label="Lucratividade (R$)" value={brl(vei.lucratividade)} strong />
            <Row testid="rs-vei-tv" label="Ticket Médio s/ Venda" value={brl(vei.ticket_venda)} />
            <Row testid="rs-vei-tl" label="Ticket Médio p/ Lucro" value={brl(vei.ticket_lucro)} />
          </Card>
        </div>
      )}
    </div>
  );
}
